class AoiProductEditorial extends HTMLElement {
  connectedCallback() {
    this.picker = document.querySelector('variant-picker[ref="mainVariantPicker"]');
    this.woodCards = [...this.querySelectorAll('[data-aoi-wood-value]')];
    this.tabs = [...this.querySelectorAll('.aoi-tabs a')];
    this.onChange = this.syncWood.bind(this);
    this.picker?.addEventListener('change', this.onChange);
    this.woodCards.forEach((card) => card.addEventListener('click', () => this.selectWood(card.dataset.aoiWoodValue)));
    this.querySelectorAll('[data-aoi-gallery-direction]').forEach((button) => {
      button.addEventListener('click', () => this.scrollGallery(Number(button.dataset.aoiGalleryDirection)));
    });
    this.reorderWoodOptions();
    this.syncWood();
    this.cleanProductDescription();
    this.completeDescriptionOpening();
    this.insertAoiLivingStoryImage();
    this.moveAoiLivingStoryToCraftSection();
    this.moveDescriptionImages();
    this.positionHeroControls();
    this.setupHeroDots();
    this.setupLuxComparison();
    this.observeSections();
  }

  disconnectedCallback() {
    this.picker?.removeEventListener('change', this.onChange);
    this.observer?.disconnect();
  }

  woodInputs() {
    return [...(this.picker?.querySelectorAll('input[data-option-name], select[name^="options"]') || [])]
      .filter((input) => (input.dataset.optionName || input.name || '').toLowerCase().includes('wood'));
  }

  reorderWoodOptions() {
    if (!['aoi-copenhagen-classic', 'aoi-copenhagen-low-crossbar'].includes(this.dataset.productHandle)) return;
    const order = ['Japanese Keyaki', 'Walnut', 'Oak', 'Ash'];
    const inputs = this.woodInputs().filter((input) => input.matches('input[type="radio"]'));
    if (!inputs.length) return;
    const fieldset = inputs[0].closest('fieldset');
    order.forEach((name) => {
      const input = inputs.find((item) => item.value.trim().toLowerCase() === name.toLowerCase());
      if (input) fieldset.append(input.closest('label'));
    });
  }

  selectedWood() {
    const inputs = this.woodInputs();
    const radio = inputs.find((input) => input.checked);
    const select = inputs.find((input) => input.matches('select'));
    return radio?.value || select?.value || '';
  }

  syncWood() {
    const selected = this.selectedWood().trim().toLowerCase();
    this.woodInputs().filter((input) => input.matches('input[type="radio"]')).forEach((input) => {
      input.setAttribute('data-current-checked', String(input.checked));
    });
    this.woodCards.forEach((card) => card.setAttribute('aria-pressed', String(card.dataset.aoiWoodValue.toLowerCase() === selected)));
  }

  selectWood(value) {
    const input = this.woodInputs().find((item) => item.value.trim().toLowerCase() === value.toLowerCase());
    if (!input || input.disabled || input.getAttribute('aria-disabled') === 'true') return;
    if (input.matches('select')) { input.value = value; input.dispatchEvent(new Event('change', { bubbles: true })); }
    else input.click();
    requestAnimationFrame(() => this.syncWood());
    window.setTimeout(() => this.syncWood(), 250);
  }

  cleanProductDescription() {
    const root = this.querySelector('.aoi-details__product-description');
    if (!root) return;
    const markers = [
      'bicycle design patented', 'stainless steel sus304', 'european standard testing',
      'stainless frames handmade in taiwan', 'worldwide shipping with dhl',
      'click to read more about aoi copenhagen', 'please note: each aoi.cycle bicycle',
      'https://www.youtube.com/watch?v=5cj1oacutwy', 'youtube.com/watch?v=5cj1oacutwy'
    ];
    root.querySelectorAll('p, li').forEach((node) => {
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      const lower = text.toLowerCase();
      const indices = markers.map((marker) => lower.indexOf(marker)).filter((index) => index >= 0);
      if (!indices.length) return;
      const cut = Math.min(...indices);
      if (cut === 0) node.remove();
      else node.textContent = text.slice(0, cut).trim();
    });
    root.querySelectorAll('p, li').forEach((node) => {
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      if (!text || text === '+' || /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=5cJ1OaCUTWY(?:\S*)?$/i.test(text)) node.remove();
    });
    [...root.childNodes].forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      if (!text || text === '+' || /youtube\.com\/watch\?v=5cJ1OaCUTWY/i.test(text)) node.remove();
    });
  }

  completeDescriptionOpening() {
    const root = this.querySelector('.aoi-details__product-description');
    const title = this.dataset.productTitle?.trim();
    if (!root || !title) return;
    const opening = [...root.querySelectorAll('p, li')].find((node) => node.textContent.trim());
    if (!opening) return;
    const text = opening.textContent.replace(/\s+/g, ' ').trim();
    if (!/^(?:is|are|was|were|has|have|offers|features|combines|brings|comes|provides|uses|sets|delivers)\b/.test(text)) return;
    const firstTextNode = [...opening.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (firstTextNode) firstTextNode.textContent = `${title} ${firstTextNode.textContent.trimStart()}`;
    else opening.prepend(document.createTextNode(`${title} `));
  }

  insertAoiLivingStoryImage() {
    const root = this.querySelector('.aoi-details__product-description');
    if (!root || root.querySelector('.aoi-details__story-image')) return;
    const paragraph = [...root.querySelectorAll('p, div')]
      .filter((node) => node.textContent.replace(/\s+/g, ' ').trim().startsWith('AOI.LIVING carries forward'))
      .at(-1);
    if (!paragraph) return;
    const image = document.createElement('img');
    image.className = 'aoi-details__story-image';
    image.src = 'https://cdn.store-assets.com/s/1375842/f/14860488.webp';
    image.alt = 'AOI.CYCLE handcrafted stainless steel bicycle';
    image.loading = 'lazy';
    paragraph.before(image);
  }

  moveAoiLivingStoryToCraftSection() {
    const root = this.querySelector('.aoi-details__product-description');
    const destination = this.querySelector('[data-aoi-craft-content]');
    if (!root || !destination || destination.children.length) return;
    const heading = [...root.querySelectorAll('h2, h3')].find((node) =>
      /about aoi\.living handcrafted furniture/i.test(node.textContent.trim())
    );
    if (!heading) return;
    let node = heading;
    while (node) {
      const next = node.nextSibling;
      destination.appendChild(node);
      node = next;
    }
    heading.remove();
  }

  scrollGallery(direction) {
    const track = this.querySelector('.aoi-gallery__track');
    track?.scrollBy({ left: direction * track.clientWidth * .8, behavior: 'smooth' });
  }

  moveDescriptionImages() {
    const template = this.querySelector('template[data-aoi-description-media]');
    const track = this.querySelector('.aoi-gallery__track');
    if (!template || !track) return;
    const existing = new Set([...track.querySelectorAll('img')].map((image) => image.currentSrc || image.src));
    [...template.content.querySelectorAll('img')].forEach((source) => {
      const src = source.getAttribute('src');
      if (!src || existing.has(src) || src.includes('/14860488.webp')) return;
      const item = document.createElement('div');
      item.className = 'aoi-gallery__item aoi-gallery__item--description';
      const image = source.cloneNode(true);
      image.loading = 'lazy';
      image.removeAttribute('width');
      image.removeAttribute('height');
      item.append(image);
      track.append(item);
      existing.add(src);
    });
  }

  setupHeroDots() {
    const gallery = document.querySelector('main[data-template*="product"] media-gallery');
    const controls = gallery?.querySelector('slideshow-controls');
    const counter = controls?.querySelector('.slideshow-controls__counter');
    const nativeDots = controls?.querySelector('.slideshow-controls__dots');
    const slides = [...(gallery?.querySelectorAll('slideshow-slide') || [])].filter((slide) => !slide.closest('dialog'));
    if (!controls || (!counter && !nativeDots) || slides.length < 2 || controls.querySelector('.aoi-hero-dots')) return;
    const dotCount = Math.min(10, slides.length);
    const rail = document.createElement('div');
    rail.className = 'aoi-hero-dots';
    rail.setAttribute('role', 'tablist');
    const buttons = Array.from({ length: dotCount }, (_, index) => {
      const targetIndex = Math.round(index * (slides.length - 1) / Math.max(1, dotCount - 1));
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'aoi-hero-dot';
      button.setAttribute('aria-label', `View product image ${targetIndex + 1}`);
      button.addEventListener('click', () => slides[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }));
      rail.append(button);
      return button;
    });
    const update = () => {
      const selectedNativeDot = nativeDots ? [...nativeDots.querySelectorAll('button')].findIndex((button) => button.getAttribute('aria-selected') === 'true') : -1;
      const current = selectedNativeDot >= 0
        ? selectedNativeDot
        : Math.max(0, Number(counter?.querySelector('[ref="current"]')?.textContent || 1) - 1);
      const active = Math.round(current * (dotCount - 1) / Math.max(1, slides.length - 1));
      buttons.forEach((button, index) => button.setAttribute('aria-current', String(index === active)));
    };
    if (nativeDots) nativeDots.hidden = true;
    controls.append(rail);
    new MutationObserver(update).observe(counter || nativeDots, { attributes: true, childList: true, subtree: true, characterData: true });
    update();
  }

  positionHeroControls() {
    const gallery = document.querySelector('main[data-template*="product"] media-gallery');
    const controls = gallery?.querySelector('slideshow-controls');
    const previous = gallery?.querySelector('.slideshow-control--previous');
    const next = gallery?.querySelector('.slideshow-control--next');
    if (!controls || !previous || !next || previous.parentElement === controls) return;
    controls.prepend(previous);
    controls.append(next);
  }

  setupLuxComparison() {
    const select = this.querySelector('[data-aoi-lux-select]');
    const image = this.querySelector('[data-aoi-lux-image]');
    if (!select || !image) return;
    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      if (!option?.dataset.image) return;
      image.src = option.dataset.image;
      image.alt = `${option.value} Lux comparison light field`;
    });
  }

  observeSections() {
    if (!('IntersectionObserver' in window)) return;
    const sections = [...this.querySelectorAll('.aoi-anchor')];
    this.observer = new IntersectionObserver((entries) => {
      const active = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!active) return;
      this.tabs.forEach((tab) => tab.setAttribute('aria-current', String(tab.hash === `#${active.target.id}`)));
    }, { rootMargin: '-25% 0px -60%', threshold: [0, .2, .5] });
    sections.forEach((section) => this.observer.observe(section));
  }
}

if (!customElements.get('aoi-product-editorial')) customElements.define('aoi-product-editorial', AoiProductEditorial);
